/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Button,
  useDisclosure,
  IconButton,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Text,
  Icon,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { FunnelSimple, X, GridFour, Table } from 'phosphor-react';
import { Calendar } from 'lucide-react';
import { MonthNavigator, CustomDateRangePicker } from '../../../../../components/shared';
import { InputForm } from '../../../../../components/inputs/forms/InputForm';
import SelectInputForm from '../../../../../components/inputs/forms/SelectInputForm';
import MultiSelectInputForm from '../../../../../components/inputs/forms/MultiSelectInputForm';
import { FilterDrawer } from '../../../../../components/FilterDrawer';
import { ISelect } from '../../../../../types/shared';
import { useCompanies } from '../../../../../hooks/useCompanies';
import { getFormattedLabelString } from '../../../../../utils/FormatFunctions';
import { FilterFormValues, PeriodType } from '../../../../../hooks/useTaxFilters';
import { PeriodButtonsMontly } from '../../../../../components/shared/PeriodButtonsMonthly';
import {
  DropdownSearch,
  ISearchDropdownItem,
} from '../../../../../components/shared/DropdownSearch';

// Função para formatar período
const formatPeriodo = (dateStart?: string, dateEnd?: string) => {
  if (!dateStart || !dateEnd) return null;
  const start = moment(dateStart).format('DD/MM/YYYY');
  const end = moment(dateEnd).format('DD/MM/YYYY');
  return `${start} a ${end}`;
};

interface TaxFiltersSNProps {
  filters: URLSearchParams;
  currentFilterDate: moment.Moment;
  selectedPeriod: PeriodType;
  useCustomRange: boolean;
  customDateStart: string;
  customDateEnd: string;
  activeFiltersCount: number;
  viewMode: 'grid' | 'table';
  // eslint-disable-next-line no-unused-vars
  onViewModeChange: (mode: 'grid' | 'table') => void;
  handlePrevMonth: () => void;
  handleNextMonth: () => void;
  // eslint-disable-next-line no-unused-vars
  handleSelectMonth: (year: number, month: number) => void;
  handleQuarterPeriod: () => void;
  handleSemesterPeriod: () => void;
  handleYearPeriod: () => void;
  handleCurrentYearPeriod: () => void;
  handleLastYearPeriod: () => void;
  handleApplyCustomRange: () => void;
  handleResetToMonth: () => void;
  // eslint-disable-next-line no-unused-vars
  setCustomDateStart: (date: string) => void;
  // eslint-disable-next-line no-unused-vars
  setCustomDateEnd: (date: string) => void;
  // eslint-disable-next-line no-unused-vars
  handleApplyFilters: (values: FilterFormValues) => void;
  handleClearFilters: () => void;
  // eslint-disable-next-line no-unused-vars
  onSearchChange: (search: string, searchType: string) => void;
}

export function TaxFiltersSN({
  filters,
  currentFilterDate,
  selectedPeriod,
  useCustomRange,
  customDateStart,
  customDateEnd,
  activeFiltersCount,
  viewMode,
  onViewModeChange,
  handlePrevMonth,
  handleNextMonth,
  handleSelectMonth,
  handleQuarterPeriod,
  handleSemesterPeriod,
  handleYearPeriod,
  handleCurrentYearPeriod,
  handleLastYearPeriod,
  handleApplyCustomRange,
  handleResetToMonth,
  setCustomDateStart,
  setCustomDateEnd,
  handleApplyFilters,
  handleClearFilters,
  onSearchChange,
}: TaxFiltersSNProps) {
  const { getCompaniesFilters } = useCompanies();

  const searchFiltersDropdown: ISearchDropdownItem[] = [
    { label: 'ID', value: 'external_id' },
    { label: 'CNPJ', value: 'cnpj' },
    { label: 'Razão Social', value: 'razao_social' },
  ];

  const [selectedFilter, setSelectedFilter] = useState<ISearchDropdownItem>(
    searchFiltersDropdown[0],
  );

  // Modo visual do filtro de data dentro do popover
  const [dateMode, setDateMode] = useState<'month' | 'quick' | 'custom'>('month');

  const handleFilterChange = (value: ISearchDropdownItem | null) => {
    if (value) {
      setSelectedFilter(value);
    }
  };

  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onFilterDrawerOpen,
    onClose: onFilterDrawerClose,
  } = useDisclosure();

  const filtersFormMethods = useForm<FilterFormValues & { search: string }>({
    mode: 'onChange',
    defaultValues: {
      search: filters.get('search') ?? '',
    },
  });

  const debouncedSearch = useDebounceCallback((searchValue: string) => {
    onSearchChange(searchValue, selectedFilter.value);
  }, 500);

  useEffect(() => {
    const subscription = filtersFormMethods.watch((values, { name }) => {
      if (name === 'search') {
        debouncedSearch(values.search || '');
      }
    });

    return () => subscription.unsubscribe();
  }, [filtersFormMethods, debouncedSearch, selectedFilter]);

  const hasActiveFilters = activeFiltersCount > 0;

  const queryKeyCompaniesFilters = ['CompaniesFilters-taxAnalysisSN'];
  const { data: CompaniesFiltersData } = useQuery({
    queryKey: queryKeyCompaniesFilters,
    queryFn: getCompaniesFilters,
  });

  const CompaniesFilters = CompaniesFiltersData;

  const onApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    handleApplyFilters(values);
    onFilterDrawerClose();
  };

  const onClearFilters = () => {
    handleClearFilters();
    filtersFormMethods.reset({
      search: filters.get('search') || '',
    });
  };

  return (
    <Box mb={6} p={{ base: 2, md: 4 }} bg="gray.50" borderRadius="lg">
      <FormProvider {...filtersFormMethods}>
        <Flex direction="column" gap={{ base: 2, md: 4 }}>
          {/* Linha principal: busca à esquerda, Data/Filtros à direita */}
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            gap={{ base: 2, md: 3 }}
            justify="space-between"
            w="100%"
          >
            <HStack spacing={2} flexWrap={{ base: 'wrap', md: 'nowrap' }} flex="1 1 auto" minW={0}>
              <DropdownSearch
                options={searchFiltersDropdown}
                value={selectedFilter}
                onChange={handleFilterChange}
              />
              <Box
                flex="1"
                minW={{ base: '120px', md: '250px' }}
                maxW={{ base: '100%', md: '300px' }}
              >
                <InputForm
                  name="search"
                  placeholder="Buscar..."
                  boxShadow="0px 2px 4px #1E293B0D"
                  controlMb={0}
                />
              </Box>
              {filtersFormMethods.watch('search') && (
                <Tooltip label="Limpar busca" hasArrow>
                  <IconButton
                    aria-label="Limpar busca"
                    icon={<X size={16} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => {
                      filtersFormMethods.setValue('search', '');
                      onSearchChange('', selectedFilter.value);
                    }}
                  />
                </Tooltip>
              )}
            </HStack>

            <HStack
              spacing={{ base: 1, md: 3 }}
              flexWrap="wrap"
              justify={{ base: 'flex-start', lg: 'flex-end' }}
              align="center"
            >
              {/* Exibir período do lado esquerdo do botão Data */}
              {formatPeriodo(
                filters.get('date_start') || undefined,
                filters.get('date_end') || undefined,
              ) && (
                <Text
                  fontSize={{ base: '2xs', md: 'xs' }}
                  color="gray.600"
                  fontWeight="500"
                  whiteSpace="nowrap"
                  display={{ base: 'none', sm: 'block' }}
                >
                  Período:{' '}
                  {formatPeriodo(
                    filters.get('date_start') || undefined,
                    filters.get('date_end') || undefined,
                  )}
                </Text>
              )}
              {/* Filtro de Data como botão clicável com modos organizados */}
              <Popover
                placement="bottom-start"
                onOpen={() => {
                  // Sincroniza o modo visual com o estado atual de período
                  if (useCustomRange) {
                    setDateMode('custom');
                  } else if (selectedPeriod === 'month') {
                    setDateMode('month');
                  } else {
                    setDateMode('quick');
                  }
                }}
              >
                <PopoverTrigger>
                  <Button variant="outline" leftIcon={<Calendar size={16} />} size="sm" bg="white">
                    Data
                  </Button>
                </PopoverTrigger>
                <PopoverContent w={{ base: 'calc(100vw - 32px)', sm: 'auto' }} maxW="640px">
                  <PopoverBody p={3}>
                    <Flex direction="column" gap={3}>
                      {/* Abas simples para trocar entre os modos de data */}
                      <HStack spacing={2} flexWrap="wrap">
                        <Button
                          size="xs"
                          variant={dateMode === 'month' ? 'solid' : 'outline'}
                          colorScheme="blue"
                          onClick={() => setDateMode('month')}
                          flex={{ base: '1 1 auto', sm: '0 0 auto' }}
                          minW={{ base: '100px', sm: 'auto' }}
                        >
                          Mês / Ano
                        </Button>
                        <Button
                          size="xs"
                          variant={dateMode === 'quick' ? 'solid' : 'outline'}
                          colorScheme="blue"
                          onClick={() => setDateMode('quick')}
                          flex={{ base: '1 1 auto', sm: '0 0 auto' }}
                          minW={{ base: '100px', sm: 'auto' }}
                        >
                          Períodos rápidos
                        </Button>
                        <Button
                          size="xs"
                          variant={dateMode === 'custom' ? 'solid' : 'outline'}
                          colorScheme="blue"
                          onClick={() => setDateMode('custom')}
                          flex={{ base: '1 1 auto', sm: '0 0 auto' }}
                          minW={{ base: '100px', sm: 'auto' }}
                        >
                          Período personalizado
                        </Button>
                      </HStack>

                      {/* Conteúdo dos modos de data - exibidos de forma isolada */}
                      {dateMode === 'month' && (
                        <Box w="100%">
                          <HStack spacing={1} flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
                            <Box
                              flex={{ base: '1 1 100%', sm: '0 0 auto' }}
                              minW={{ base: '100%', sm: 'auto' }}
                            >
                              <MonthNavigator
                                currentDate={currentFilterDate}
                                onPrevMonth={handlePrevMonth}
                                onNextMonth={handleNextMonth}
                                onSelectMonth={handleSelectMonth}
                                isDisabled={selectedPeriod !== 'month' || useCustomRange}
                              />
                            </Box>
                            {(selectedPeriod !== 'month' || useCustomRange) && (
                              <Tooltip label="Voltar para mês atual" hasArrow>
                                <IconButton
                                  aria-label="Limpar filtro de período"
                                  icon={<X size={16} />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={handleResetToMonth}
                                  flexShrink={0}
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Box>
                      )}

                      {dateMode === 'quick' && (
                        <Box w="100%">
                          <PeriodButtonsMontly
                            selectedPeriod={selectedPeriod}
                            onQuarterClick={handleQuarterPeriod}
                            onSemesterClick={handleSemesterPeriod}
                            onYearClick={handleYearPeriod}
                            onCurrentYearClick={handleCurrentYearPeriod}
                            onLastYearClick={handleLastYearPeriod}
                          />
                        </Box>
                      )}

                      {dateMode === 'custom' && (
                        <Box w="100%">
                          <CustomDateRangePicker
                            isActive={useCustomRange}
                            dateStart={customDateStart}
                            dateEnd={customDateEnd}
                            onDateStartChange={setCustomDateStart}
                            onDateEndChange={setCustomDateEnd}
                            onApply={handleApplyCustomRange}
                          />
                        </Box>
                      )}
                    </Flex>
                  </PopoverBody>
                </PopoverContent>
              </Popover>

              <HStack
                spacing={1}
                bg="white"
                px={1}
                borderRadius="md"
                border="1px"
                borderColor="gray.200"
              >
                <IconButton
                  aria-label="Visualização em tabela"
                  icon={<Icon as={Table} />}
                  onClick={() => onViewModeChange('table')}
                  size="sm"
                  variant={viewMode === 'table' ? 'solid' : 'ghost'}
                  colorScheme={viewMode === 'table' ? 'blue' : 'gray'}
                />
                <IconButton
                  aria-label="Visualização em grid"
                  icon={<Icon as={GridFour} />}
                  onClick={() => onViewModeChange('grid')}
                  size="sm"
                  variant={viewMode === 'grid' ? 'solid' : 'ghost'}
                  colorScheme={viewMode === 'grid' ? 'blue' : 'gray'}
                />
              </HStack>

              <Button
                onClick={onFilterDrawerOpen}
                leftIcon={<FunnelSimple size={16} />}
                variant="outline"
                colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                size="sm"
              >
                Filtros{hasActiveFilters && ` (${activeFiltersCount})`}
              </Button>
            </HStack>
          </Flex>
        </Flex>

        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={onFilterDrawerClose}
          title="Filtros Avancados"
          hasActiveFilters={hasActiveFilters}
          onClearFilters={onClearFilters}
          onApplyFilters={onApplyFilters}
          formMethods={filtersFormMethods}
          size="lg"
        >
          <form className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SelectInputForm<ISelect<string>>
              name="cidade"
              placeholder="Filtrar por cidade"
              options={getFormattedLabelString(CompaniesFilters?.cidade)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="uf"
              placeholder="Filtrar por Estado"
              options={getFormattedLabelString(CompaniesFilters?.uf)}
              controlMb={0}
            />
            <MultiSelectInputForm<ISelect<string>>
              name="grupo_de_empresas"
              placeholder="Selecione até 4 grupos"
              options={getFormattedLabelString(CompaniesFilters?.grupo_de_empresas)}
              maxSelectedOptions={4}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="tags"
              placeholder="Filtrar por tags"
              options={getFormattedLabelString(CompaniesFilters?.tags)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="comercial"
              placeholder="Filtrar por comercial"
              options={getFormattedLabelString(CompaniesFilters?.comercial)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="contabil"
              placeholder="Filtrar pelo contabil"
              options={getFormattedLabelString(CompaniesFilters?.contabil)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="financeiro"
              placeholder="Filtrar pelo financeiro"
              options={getFormattedLabelString(CompaniesFilters?.financeiro)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="contas_a_pagar"
              placeholder="Filtrar por contas a pagar"
              options={getFormattedLabelString(CompaniesFilters?.contas_a_pagar)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="encerramento_anual"
              placeholder="Filtrar por encerramento anual"
              options={getFormattedLabelString(CompaniesFilters?.encerramento_anual)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="fiscal"
              placeholder="Filtrar por fiscal"
              options={getFormattedLabelString(CompaniesFilters?.fiscal)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="imposto_de_renda"
              placeholder="filtrar por imposto de renda"
              options={getFormattedLabelString(CompaniesFilters?.imposto_de_renda)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="integracao_contabil"
              placeholder="Filtrar por integracao contabil"
              options={getFormattedLabelString(CompaniesFilters?.integracao_contabil)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="integracao_fiscal"
              placeholder="Filtrar por integracao fiscal"
              options={getFormattedLabelString(CompaniesFilters?.integracao_fiscal)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="pessoal"
              placeholder="Filtrar por pessoal"
              options={getFormattedLabelString(CompaniesFilters?.pessoal)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="relacionamento_com_cliente"
              placeholder="Filtrar por relacionamento com cliente"
              options={getFormattedLabelString(CompaniesFilters?.relacionamento_com_cliente)}
              controlMb={0}
            />
            <SelectInputForm<ISelect<string>>
              name="societario"
              placeholder="Filtrar por societario"
              options={getFormattedLabelString(CompaniesFilters?.societario)}
              controlMb={0}
            />
          </form>
        </FilterDrawer>
      </FormProvider>
    </Box>
  );
}
